/* eslint-disable no-console */
import axios from 'axios';
import { authUserStorageKey, psiAuthStorageKey } from '@/configs/main';
import {
	getCurrentBranch,
	getCurrentMerchant,
} from '@/helpers/WorkspaceHelper';

/**
 * PSI (server.komodo) session for the portal.
 *
 * The portal's echidna token is exchanged once per merchant for a komodo Bearer
 * token via POST /komodo-api/api/auth/exchange-token. The komodo token is
 * issued without a company (no `bid` claim) so the company scope is chosen per
 * request through the `BranchId` header, which komodo reads as its own
 * DeptCode - never a ZiiCloud branch id.
 */

export const KOMODO_API_PREFIX = '/komodo-api';
export const KOMODO_APPLICATION = 'PORTAL';
export const KOMODO_CODE = {
	OK: 1000,
	FAIL: 1001,
	ERROR: 1002,
	PWD_CHANGED: 1012,
	UNAUTHORIZED: 1401,
	NOT_FOUND: 1404,
	CONFLICT: 1409,
};

// Refresh the komodo token when less than one day of its 7-day life is left.
const REFRESH_AHEAD_MS = 24 * 60 * 60 * 1000;

// Bare axios instance for the auth endpoints only: no portal headers, no
// interceptors, so the ZiiCloud `Token` / `BranchId` headers never reach komodo.
const authClient = axios.create({
	baseURL: KOMODO_API_PREFIX,
	timeout: 60000,
});

const readStorage = (key) => {
	try {
		const value = window.targetStorage.getItem(key);
		return value ? JSON.parse(value) : null;
	} catch (error) {
		console.error('>>> KomodoAuth -> readStorage error:', error);
		return null;
	}
};

const writeStorage = (key, value) => {
	try {
		if (value) {
			window.targetStorage.setItem(key, JSON.stringify(value));
		} else {
			window.targetStorage.removeItem(key);
		}
	} catch (error) {
		console.error('>>> KomodoAuth -> writeStorage error:', error);
	}
};

// komodo serialises camelCase but the portal convention tolerates PascalCase.
const field = (obj, name) => {
	if (!obj) return undefined;
	if (obj[name] !== undefined) return obj[name];
	return obj[name.charAt(0).toUpperCase() + name.slice(1)];
};

export const createKomodoError = (code, message, payload) => {
	const error = new Error(message || `PSI request failed (${code})`);
	error.code = code;
	error.payload = payload;
	error.isKomodoError = true;
	return error;
};

export const isKomodoError = (error) => !!(error && error.isKomodoError);

/**
 * Unwrap komodo's `{code, message, data}` envelope (HTTP 200 even on failure).
 * Unlike the portal HttpClient, a falsy `data` (false / 0) is returned as-is
 * because komodo bool endpoints legitimately answer `false`.
 */
export const unwrapEnvelope = (body) => {
	const code = parseInt(field(body, 'code'), 10);
	if (code === KOMODO_CODE.OK) {
		const data = field(body, 'data');
		return data === undefined || data === null ? true : data;
	}
	throw createKomodoError(code, field(body, 'message'), body);
};

const getPortalToken = () => {
	const user = readStorage(authUserStorageKey);
	if (!user) return null;
	// In-store users carry a separate gateway token for cloud services.
	return user.type === 'local' ? user.gatewayToken : user.token;
};

const getMerchantId = () => {
	try {
		const merchant = getCurrentMerchant();
		return merchant ? merchant.merchantId : null;
	} catch (error) {
		return null;
	}
};

const getPortalBranchId = () => {
	try {
		const branch = getCurrentBranch();
		return branch ? branch.branchId : null;
	} catch (error) {
		return null;
	}
};

const pickDefaultDept = (depts) => {
	if (!depts || depts.length === 0) return null;
	const branchId = getPortalBranchId();
	const matched = depts.find((d) => branchId && d.branchID === branchId);
	return (matched || depts[0]).deptCode;
};

const toSession = (data, merchantId, previous) => {
	const jwt = JSON.parse(field(data, 'token'));
	const depts = field(data, 'depts') || (previous && previous.depts) || [];
	return {
		merchantId,
		merchantID: field(data, 'merchantID') || merchantId,
		userCode: field(data, 'userCode'),
		userName: field(data, 'userName'),
		email: field(data, 'email'),
		authToken: jwt.auth_token,
		refreshToken: jwt.refresh_token,
		refreshHash: jwt.refresh_hash,
		expiresAt: Date.now() + jwt.expires_in * 1000,
		depts: depts.map((d) => ({
			deptCode: field(d, 'deptCode'),
			deptName: field(d, 'deptName'),
			branchID: field(d, 'branchID'),
			timeZone: field(d, 'timeZone'),
		})),
		deptCode: previous ? previous.deptCode : null,
		userFunctions:
			field(data, 'userFunctions') ||
			(previous && previous.userFunctions) ||
			null,
		isMerchantFirstLogin: !!field(data, 'isMerchantFirstLogin'),
	};
};

let session = readStorage(psiAuthStorageKey);
let inflight = null;

const isValidFor = (s, merchantId) =>
	!!(
		s &&
		s.merchantId === merchantId &&
		s.authToken &&
		s.expiresAt > Date.now()
	);

const needsRefresh = (s) => s.expiresAt - Date.now() < REFRESH_AHEAD_MS;

const exchange = async (merchantId) => {
	const portalToken = getPortalToken();
	if (!portalToken) {
		throw createKomodoError(
			KOMODO_CODE.UNAUTHORIZED,
			'Portal user is not logged in'
		);
	}
	const res = await authClient.post('/api/auth/exchange-token', {
		Token: portalToken,
		MerchantID: merchantId,
		Application: KOMODO_APPLICATION,
	});
	const next = toSession(unwrapEnvelope(res.data), merchantId, null);
	next.deptCode = pickDefaultDept(next.depts);
	return next;
};

const refresh = async (current) => {
	const res = await authClient.post('/api/auth/refresh-token', {
		MerchantID: current.merchantID,
		RefreshToken: current.refreshToken,
		RefreshHash: current.refreshHash,
		Token: current.authToken,
		UserCode: current.userCode,
		Application: KOMODO_APPLICATION,
	});
	return toSession(unwrapEnvelope(res.data), current.merchantId, current);
};

const persist = (next) => {
	session = next;
	writeStorage(psiAuthStorageKey, next);
	return next;
};

/**
 * Return a usable komodo session for the current merchant, exchanging or
 * refreshing as needed. Concurrent callers share one in-flight request.
 */
export const ensureSession = async ({ force = false } = {}) => {
	const merchantId = getMerchantId();
	if (!merchantId) {
		throw createKomodoError(KOMODO_CODE.FAIL, 'No merchant selected');
	}
	if (!force && isValidFor(session, merchantId) && !needsRefresh(session)) {
		return session;
	}
	if (inflight) return inflight;
	inflight = (async () => {
		let next = null;
		if (!force && isValidFor(session, merchantId)) {
			try {
				next = await refresh(session);
			} catch (error) {
				console.warn('>>> KomodoAuth -> refresh failed, re-exchange:', error);
				next = null;
			}
		}
		if (!next) {
			next = await exchange(merchantId);
		}
		const keepDept =
			session &&
			session.merchantId === merchantId &&
			session.deptCode &&
			next.depts.some((d) => d.deptCode === session.deptCode);
		if (keepDept) {
			next.deptCode = session.deptCode;
		}
		return persist(next);
	})();
	try {
		return await inflight;
	} finally {
		inflight = null;
	}
};

export const getSession = () => session;

export const clearSession = () => {
	persist(null);
};

export const getDeptScope = () => (session ? session.deptCode : null);

export const getDepts = () => (session ? session.depts : []);

// komodo converts every DateTime to the X-Timezone header (CustomDateTimeConverter). Without a company
// time zone the portal showed UTC (QA #112); the browser zone then showed the tester's own zone instead
// of the store's (QA #246), so the ZiiCloud branch and merchant zones come first.
const portalTimezone = () => {
	try {
		const branch = getCurrentBranch();
		if (branch && branch.timezone) return branch.timezone;
		const merchant = getCurrentMerchant();
		return (merchant && merchant.timezone) || '';
	} catch (e) {
		return '';
	}
};
const browserTimezone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
	} catch (e) {
		return '';
	}
};

export const getDeptTimezone = () => {
	const dept =
		session && session.deptCode
			? session.depts.find((d) => d.deptCode === session.deptCode)
			: null;
	return (dept && dept.timeZone) || portalTimezone() || browserTimezone();
};

/**
 * Switch the company (DeptCode) scope used by every subsequent komodo call.
 * The komodo token is not re-issued: scope travels in the BranchId header.
 */
export const setDeptScope = (deptCode) => {
	if (!session) return null;
	if (deptCode && !session.depts.some((d) => d.deptCode === deptCode)) {
		throw createKomodoError(
			KOMODO_CODE.FAIL,
			`Company ${deptCode} is not available to the current user`
		);
	}
	return persist({ ...session, deptCode: deptCode || null });
};

export const hasUserFunction = (functionId) =>
	!!(
		session &&
		session.userFunctions &&
		session.userFunctions.indexOf(functionId) >= 0
	);
